import { StemType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { getSnapshot } from 'mobx-state-tree'
import { FC, useContext } from 'react'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { GRAY_COLORS } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { Box, Column, Image, Row } from '../../core/Components'
import LoadingAnimation from '../../core/LoadingAnimation'
import ProfileAvatar from '../../platform/explore/common/ProfileAvatar'
import RandomizeButton from '../../studio/controls/RandomizeButton'
import SoloButton from '../../studio/controls/SoloButton'
import VolumeSlider from '../../studio/controls/VolumeSlider'
import { ClipboardContext } from '../Context'
import ContextMenu from '../controls/ContextMenu'
import { CopyTypes } from '../StudioConstants'
import { getStemDisplayData } from '../StudioUtils'
import BetweenTracksDropzone from './BetweenTracksDropzone'
import useInstrumentTrackHeader from './useInstrumentTrackHeader'

interface TrackHeaderContextMenuProps {
  stemIndex: number
  deleteStem: () => void
}
const TrackHeaderContextMenu: FC<TrackHeaderContextMenuProps> = observer(
  ({ stemIndex, deleteStem }) => {
    const { studio } = useTreeState()
    const { clipboard, setClipboard } = useContext(ClipboardContext)
    const stem = studio.song.stems[stemIndex]
    const isRemixed = stem.isRemixed
    const isDrums = stem.type() === StemType.DRUMS

    return (
      <ContextMenu.Portal>
        <ContextMenu.Content>
          {!isDrums && (
            <ContextMenu.Item
              onClick={() => {
                setClipboard({
                  type:
                    stem.type() === StemType.AUDIO ? CopyTypes.AUDIOSTEM : CopyTypes.MELODICSTEM,
                  value: getSnapshot(studio.song.stems[stemIndex])
                })
              }}
            >
              Copy
            </ContextMenu.Item>
          )}
          {clipboard &&
            (clipboard.type === CopyTypes.MELODICSTEM ||
              clipboard.type === CopyTypes.AUDIOSTEM) && (
              <ContextMenu.Item
                onClick={() => {
                  studio.disableAudioUpdates()
                  studio.song.stems.push(clipboard.value)
                  studio.song.stems[studio.song.stems.length - 1].generateID()
                  studio.song.changeStemIndex(studio.song.stems.length - 1, stemIndex)
                  studio.enableAudioUpdates()
                }}
              >
                Paste
              </ContextMenu.Item>
            )}
          {!isRemixed && (
            <ContextMenu.Item
              onClick={() => {
                studio.disableAudioUpdates()
                studio.clearStemArrangement(stemIndex)
                studio.enableAudioUpdates()
              }}
            >
              Clear
            </ContextMenu.Item>
          )}
          <ContextMenu.Item onClick={() => deleteStem()}>Delete</ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    )
  }
)

interface InstrumentTrackHeaderControlsProps {
  stemIndex: number
  isSolo: boolean
  isHover: boolean
  isDragging: boolean
  setForbidDrag: (value: boolean) => void
  deleteStem: () => void
}
const InstrumentTrackHeaderControls: FC<InstrumentTrackHeaderControlsProps> = observer(
  ({ stemIndex, isSolo, isHover, deleteStem, isDragging, setForbidDrag }) => {
    const { studio } = useTreeState()
    const stem = studio.song.stems[stemIndex]
    const isRemixed = stem.isRemixed
    return (
      <Row css={{ height: '100%' }}>
        <Row verticalCenter justifyContentEnd>
          {!isRemixed && stem.type() !== StemType.AUDIO && (
            <RandomizeButton
              randomize={() => {
                studio.randomizeStem(stemIndex)
              }}
              className="me-2"
            />
          )}
          <SoloButton
            solo={() => studio.setStemSolo(stemIndex)}
            unsolo={() => studio.removeStemSolo()}
            className="me-2"
            isSelected={isSolo}
          />
          <VolumeSlider
            onMouseEnter={() => setForbidDrag(!isDragging && true)}
            onMouseLeave={() => setForbidDrag(false)}
            value={studio.getStemGain(stemIndex)}
            setGain={(gain) => {
              studio.updateStemGain(stemIndex, gain)
            }}
            width="80px"
          />
        </Row>
        <Column style={{ width: '14px', marginLeft: '4px', height: '100%', marginTop: '4px' }}>
          {isHover && (
            <Image
              onClick={(e) => {
                e.stopPropagation()
                deleteStem()
              }}
              src="/static/icons/close_black_24dp.svg"
              css={{ width: '100%', cursor: 'pointer' }}
            />
          )}
        </Column>
      </Row>
    )
  }
)

interface InstrumentTrackHeaderTitleProps {
  stemIndex: number
  openSampleDrawer: () => void
}
const InstrumentTrackHeaderTitle: FC<InstrumentTrackHeaderTitleProps> = observer(
  ({ stemIndex, openSampleDrawer }) => {
    const { studio } = useTreeState()
    const stem = studio.song.stems[stemIndex]
    const isDrums = stem.type() === StemType.DRUMS
    const { category, details, isRemixed, remixedStemId } = getStemDisplayData(studio, stemIndex)
    const stemTitle = isDrums ? 'Drums' : `${details}`
    const eth = isRemixed ? ArpRegistry.retrieveFromCache(remixedStemId)?.user.walletAddress : ''
    return (
      <Row className="ellipsis-overflow" verticalCenter>
        {isRemixed && (
          <ProfileAvatar
            onClick={() => eth && window.open(`/profile/${eth}`, '_blank')}
            eth={eth}
            css={{ width: '20px', height: '20px', minWidth: '20px', marginRight: '12px' }}
          />
        )}
        <Box className="ellipsis-overflow" title={stemTitle}>
          <Box
            css={{
              fontSize: '14px',
              fontWeight: '700',
              lineHeight: '20px',
              transition: '150ms'
            }}
            className="ellipsis-overflow"
          >
            {category || <LoadingAnimation />}
          </Box>
          <Box
            css={{
              opacity: '0.6',
              fontSize: '12px',
              fontWeight: '500',
              lineHeight: '16px',
              '&:hover': {
                textDecoration: !isDrums && 'underline'
              }
            }}
            className="ellipsis-overflow"
            onClick={(e) => {
              if (isDrums) return
              e.stopPropagation()
              openSampleDrawer()
            }}
          >
            {details}
          </Box>
        </Box>
      </Row>
    )
  }
)

interface InstrumentTrackHeaderProps {
  stemID: string
  height: string
  openSampleDrawer: () => void
}
const InstrumentTrackHeader: FC<InstrumentTrackHeaderProps> = observer(
  ({ stemID, height, openSampleDrawer }) => {
    const { studio } = useTreeState()
    const stemIndex = studio.song.getStemIndexByID(stemID)
    const {
      isTrackOver,
      isSelected,
      isOver,
      isDragging,
      setIsHover,
      ref,
      setForbidDrag,
      isHover,
      deleteStem,
      isSolo
    } = useInstrumentTrackHeader(stemIndex)
    return (
      <ContextMenu.Root>
        <ContextMenu.Trigger>
          <Box
            css={{
              width: '100%',
              height: height,
              minHeight: height,
              position: 'relative',
              borderBottom: '1px solid var(--studio-medium)'
            }}
          >
            <BetweenTracksDropzone stemIndex={stemIndex} forceGlow={isTrackOver} />
            <Row
              verticalCenter
              justifySpaceBetween
              className="unselectable fw-medium"
              style={{
                width: '100%',
                backgroundColor: isSelected || isOver ? GRAY_COLORS.gray005 : GRAY_COLORS.gray01,
                height: '100%',
                paddingLeft: '24px',
                paddingRight: '6px',
                verticalAlign: 'middle',
                filter: isSolo && 'contrast(120%)',
                visibility: isDragging ? 'hidden' : 'visible'
              }}
              onClick={() => studio.setSelectedStemIndex(stemIndex)}
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
              ref={ref}
            >
              <InstrumentTrackHeaderTitle
                stemIndex={stemIndex}
                openSampleDrawer={openSampleDrawer}
              />
              <InstrumentTrackHeaderControls
                stemIndex={stemIndex}
                isSolo={isSolo}
                isHover={isHover}
                deleteStem={deleteStem}
                setForbidDrag={setForbidDrag}
                isDragging={isDragging}
              />
            </Row>
          </Box>
        </ContextMenu.Trigger>

        <TrackHeaderContextMenu stemIndex={stemIndex} deleteStem={deleteStem} />
      </ContextMenu.Root>
    )
  }
)

export default InstrumentTrackHeader
